/**
 * MarginReconcile — the card strip again, as rows, showing why its four numbers do not
 * subtract and what they look like when they do.
 *
 * ⚠ THE QUESTION IT ANSWERS. The strip reads £2,847,905 · £1,884,814 · £867,136, and
 * £2,847,905 − £1,884,814 is £963,091. Margin is taken on the part that carries a cost, and
 * £95,955.51 of billed value carries none. Stating that as prose did not land — a reader who
 * has just done a subtraction wants the subtraction fixed, in the same shape they did it.
 *
 * ⭐ EVERY ROW TALLIES, AND THE ROWS ADD UP (Mike, 24 Aug 2026). The first attempt opened with
 * the card strip reprinted verbatim, which was two mistakes at once: the reader can already
 * see the cards, and that row does not subtract — reprinting a row that does not tally, inside
 * a table built to make things tally, defeats the table. So the population is SPLIT instead.
 * Costed lines: £2,751,950 − £1,884,814 = £867,136, at 31.5%. Lines we cannot cost:
 * £95,956 − £95,956 = £0. Add them: £2,847,905 − £1,980,769 = £867,136 at 30.4%. Each line
 * reads across, and the first two read down into the third.
 *
 * ⚠ THE COST COLUMN ON ROW TWO IS AN ASSUMPTION WEARING A MEASUREMENT'S CLOTHES, which is why
 * the row says "assumed to earn nothing" on its face. A line we cannot cost earns no margin
 * that we can claim, and a line earning no margin has, in effect, a cost equal to its revenue.
 *
 * ⚠ IN EFFECT — NOT MEASURED. Those 23 lines might have run at any margin; we cannot say,
 * which is the entire reason they are held out. So the reconciled row is the CONSERVATIVE
 * reading, and the two GM percentages bracket the truth rather than compete: 30.4% if the
 * uncosted work earned nothing, 31.5% if it earned what the rest did. The panel says so
 * instead of picking one.
 *
 * The last row is Sage, because the same four questions have a ledger answer and a reader
 * comparing them should not have to hold two screens in their head.
 */

import React from 'react'
import { formatCurrency } from '../utils/formatters'
import { EM_DASH } from './kit/cellFormat'
import type { SageWindow } from '../viewmodels/marginSage'
import type { BreakdownTiles } from '../viewmodels/orderBook'

const gbp = (v: number | null): string => (v === null ? EM_DASH : formatCurrency(v, '£', 0))
const pct = (v: number | null): string => (v === null ? EM_DASH : `${v.toFixed(1)}%`)

const HEAD = 'px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[.07em] text-mut2'
const CELL = 'px-3 py-1.5 text-right tabular-nums text-[12px]'

interface Row {
  label: string
  note?: string
  value: string
  cost: string
  gm: string
  gmPct: string
  strong?: boolean
  muted?: boolean
}

function rowsOf(
  tiles: BreakdownTiles, uncosted: number, uncostedLines: number, sage: SageWindow
): Row[] {
  const costed = tiles.costedValue ?? 0
  const adjusted = tiles.cost === null ? null : tiles.cost + uncosted
  const onBilled = adjusted === null || tiles.value === 0
    ? null
    : (100 * (tiles.value - adjusted)) / tiles.value
  const sageGm = sage.revenue === null || sage.grossProfit === null || sage.revenue === 0
    ? null
    : (100 * sage.grossProfit) / sage.revenue
  return [
    {
      label: 'Lines we can cost',
      note: 'a job or a quote behind every one — the margin the cards report',
      value: gbp(costed), cost: gbp(tiles.cost), gm: gbp(tiles.gm), gmPct: pct(tiles.gmPct),
    },
    {
      label: 'Lines we cannot cost',
      note: `${uncostedLines.toLocaleString('en-GB')} lines · no job and no quote, so assumed to earn nothing`,
      value: gbp(uncosted), cost: gbp(uncosted), gm: gbp(0), gmPct: pct(0),
      muted: true,
    },
    {
      label: '= All billed value',
      note: 'what the Value Generated card shows, and the margin measured against it',
      value: gbp(tiles.value), cost: gbp(adjusted), gm: gbp(tiles.gm), gmPct: pct(onBilled),
      strong: true,
    },
    {
      label: 'Sage ledger',
      note: 'the same four questions, answered by the accounts',
      value: gbp(sage.revenue), cost: gbp(sage.cogs), gm: gbp(sage.grossProfit), gmPct: pct(sageGm),
      muted: true,
    },
  ]
}

export default function MarginReconcile({ tiles, uncosted, uncostedLines, sage }: {
  tiles: BreakdownTiles
  uncosted: number
  uncostedLines: number
  sage: SageWindow
}): React.JSX.Element {
  const rows = rowsOf(tiles, uncosted, uncostedLines, sage)
  return (
    <div className="cx-scroll overflow-x-auto">
      <table className="w-full min-w-[620px] border-collapse">
        <thead>
          <tr className="border-b border-line">
            <th className={`${HEAD} text-left`} />
            <th className={`${HEAD} text-right`}>Value Generated</th>
            <th className={`${HEAD} text-right`}>Cost of Production</th>
            <th className={`${HEAD} text-right`}>Gross Margin (£)</th>
            <th className={`${HEAD} text-right`}>Gross Margin (%)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-line2">
              <td className="px-3 py-1.5">
                <div className={`text-[12px] ${r.strong ? 'font-semibold text-ink' : 'text-mut'}`}>{r.label}</div>
                {r.note && <div className="font-sans text-[10.5px] text-mut2">{r.note}</div>}
              </td>
              {[r.value, r.cost, r.gm, r.gmPct].map((cell, i) => (
                <td key={i} className={`${CELL} ${r.strong ? 'font-semibold text-ink' : 'text-mut2'}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
